import { getSearchHandle } from '@lib/data/search-handle';
import { useEffect, useRef, useState } from 'react';
import { BusinessInfo } from 'types/business';
import Slider from 'react-slick';
import ReadonlyRating from '@modules/common/components/readonly-rating';
import { YelpLogo } from '@modules/common/components/yelp-logo/yelp-logo';
import { ButtonPrimary } from '@modules/common/components/form/buttons/button-primary';
import { useRouter } from 'next/dist/client/components/navigation';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { GoogleLogo } from '@modules/common/components/google-logo';
import { ImageProduct } from './ImageProduct';
import { useAvailableCyoThemeCards } from '@lib/hooks/use-availableCyoThemeCards';
import { DEFAULT_SEARCH_PROVIDER } from '@lib/constants';
import { useSearchService } from '@lib/context/search-service-context';
import { GoogleRating } from '@modules/common/components/google-rating';

function NextArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <ChevronRightIcon />
    </div>
  );
}

function PrevArrow(props: any) {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={{ ...style, display: 'block' }} onClick={onClick}>
      <ChevronLeftIcon />
    </div>
  );
}

export const GiftCardSimilarGifts = ({ business }: { business: BusinessInfo }) => {
  const { giftCardTypesText, configurationPageImagesBusiness } = useAvailableCyoThemeCards();
  const { currentSearch } = useSearchService();
  const [data, setData] = useState<any>([]);
  const router = useRouter();
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
  const settings = {
      speed: 500,
      slidesToShow: 5,
      slidesToScroll: 1,
      variableWidth: true,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      infinite: true,
      accessibility: false,
      focusOnSelect: false,
      swipe: true,
      touchMove: true,
      draggable: true,
      responsive: [
          {
              breakpoint: 1440,
              settings: { slidesToShow: 3, slidesToScroll: 1 },
          },
          {
              breakpoint: 1210,
              settings: { slidesToShow: 2, slidesToScroll: 1 },
          },
          {
              breakpoint: 768,
              settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
              },
          },
      ],
  };

  const getData = async () => {
    let location = business?.location?.display_address?.join(', ');
    if (business?.location?.city) {
      location = `${business?.location?.city}, ${business?.location?.state} ${business?.location?.country}`;
    }
    try {
      // Use card_program if available, otherwise fall back to parent_category
      const category = business?.card_program || business?.parent_category || '';
      const response = await getSearchHandle(
        location ?? '',
        '',
        category,
        9,
        0,
        'best_match',
        true,
        business?.search_provider || currentSearch || DEFAULT_SEARCH_PROVIDER,
        '',
        null,
        true
      );
      if (response.businesses?.length > 0) {
        const cardProgram = business?.card_program || business?.parent_category;
        let addOn = [
          {
            name: `<span>Recipient's Choice</span> Local ${giftCardTypesText?.[cardProgram] || (cardProgram || 'Business')} Card`,
            image_url: configurationPageImagesBusiness?.[cardProgram],
            hasCYO: true,
            alias: '/businesses/let-them-choose',
            search_provider: 'yelp',
          },
        ];
        if (cardProgram == 'serviceprovider') {
          addOn = [
            {
              name: `<span>Recipient's Choice</span> Local Home Services Card`,
              image_url: configurationPageImagesBusiness?.choose_your_own_serviceprovider_home,
              hasCYO: true,
              alias: '/businesses/let-them-choose',
              search_provider: 'yelp',
            },
            {
              name: `<span>Recipient's Choice</span> Local Personal Services Card`,
              image_url: configurationPageImagesBusiness.choose_your_own_serviceprovider_personal,
              hasCYO: true,
              alias: '/businesses/let-them-choose',
              search_provider: 'yelp',
            },
            {
              name: `<span>Recipient's Choice</span> Local Auto Services Card`,
              image_url: configurationPageImagesBusiness.choose_your_own_serviceprovider_auto,
              hasCYO: true,
              alias: '/businesses/let-them-choose',
              search_provider: 'yelp',
            },
          ];
        }
        setData([...addOn, ...response.businesses.filter((item: any) => item.id !== business.id)]);
      }
    } catch (error) {}
  };

  const handleMouseDown = (e: any) => {
    startPos.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handleMouseMove = (e: any) => {
    const dx = Math.abs(e.clientX - startPos.current.x);
    const dy = Math.abs(e.clientY - startPos.current.y);
    if (dx > 5 || dy > 5) setIsDragging(true);
  };

  useEffect(() => {
    // Show similar gifts if we have either card_program or parent_category
    if ((business?.card_program || business?.parent_category) && giftCardTypesText) {
      getData();
    }
  }, [business, giftCardTypesText]);

  return (
    data?.length > 1 && (
      <div className="giftCardSimilarGifts slider-container">
        <h2 className="giftCardSimilarGifts-title">Similar Gift Cards</h2>
        <div onMouseDown={handleMouseDown}
             onMouseMove={handleMouseMove}
        >
          <Slider {...settings}>
            {data?.map((item: any) => (
              <div key={item?.id} className="giftCardSimilarGifts-item">
                <div className='pointer' onClick={() => {
                  if (isDragging) return;
                  // Ensure searchBy param is included - prioritize user's selected search provider from localStorage
                  const searchProvider = item.search_provider || business.search_provider || currentSearch || DEFAULT_SEARCH_PROVIDER;
                  const url = item.alias.includes('?')
                    ? `${item.alias}&searchBy=${searchProvider}`
                    : `${item.alias}?searchBy=${searchProvider}`;
                  router.push(url);
                }}>
                  <ImageProduct business={item} className="giftCardSimilarGifts-item-card" />
                </div>
                <div className="p-2.5">
                  <div className='product-title'>
                    <h3 dangerouslySetInnerHTML={{ __html: item?.name }} />
                    {!item?.hasCYO && (
                      <div className="giftCardSimilarGifts-rating">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1">
                        {business?.search_provider == 'google' ? <GoogleRating count={item?.rating} size="1rem" /> : <ReadonlyRating count={item?.rating} size="1rem" />}
                          <div className="flex items-center gap-1">
                            <strong className="inline-block">on</strong>
                            <span className="inline-block -mt-1">
                              {business?.search_provider == 'google' ? <GoogleLogo /> : <YelpLogo />}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <ButtonPrimary
                    size="small"
                    className="mt-3 !text-[15px] !py-2 w-full sm:w-auto max-w-[280px]"
                    type="button"
                    onClick={() => {
                      // Ensure searchBy param is included - prioritize user's selected search provider from localStorage
                      const searchProvider = item.search_provider || business.search_provider || currentSearch || DEFAULT_SEARCH_PROVIDER;
                      const url = item.alias.includes('?')
                        ? `${item.alias}&searchBy=${searchProvider}`
                        : `${item.alias}?searchBy=${searchProvider}`;
                      router.push(url);
                    }}
                    active={true}
                  >
                    Buy Now!
                  </ButtonPrimary>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    )
  );
};
